// B14 — the off-PadSplit member portal, with an account-level opt-in. (The
// Beta label was removed in the Oct 6 addendum; the opt-in stays.)
//
// • MemberPortalOptInButton — Members page header control: the account opt-in
//   switch (owner / team admin only; others see status).
// • useMemberPortalOptIn — GET /api/app/member-portal/opt-in.
//
// Server contract: server/routes/member-portal.ts. Invites require the opt-in;
// members who were already invited keep their portal access either way.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { MemberPortalOptInState } from "@shared/member-portal-beta";

export const MEMBER_PORTAL_OPT_IN_KEY = ["/api/app/member-portal/opt-in"] as const;

export function useMemberPortalOptIn(options: { enabled?: boolean } = {}) {
  return useQuery<MemberPortalOptInState>({
    queryKey: MEMBER_PORTAL_OPT_IN_KEY,
    enabled: options.enabled ?? true,
    staleTime: 30_000,
  });
}

/** Account-level opt-in panel (used inside the Members page popover). */
export function MemberPortalOptInPanel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const optInQuery = useMemberPortalOptIn();
  const mutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const res = await apiRequest("PUT", "/api/app/member-portal/opt-in", { enabled });
      return (await res.json()) as MemberPortalOptInState;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(MEMBER_PORTAL_OPT_IN_KEY, data);
      toast({
        title: data.optedIn ? "Member portal turned on" : "Member portal turned off",
        description: data.optedIn
          ? "You can now invite direct members to the portal."
          : "No new invites can be sent. Members you already invited keep their access.",
      });
    },
    onError: (err) => {
      toast({
        title: "Could not update the member portal setting",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  const state = optInQuery.data;
  const optedIn = state?.optedIn ?? false;
  const canManage = state?.canManage ?? false;

  return (
    <div className="space-y-3" data-testid="panel-member-portal-opt-in">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-foreground">Member portal</span>
      </div>
      <p className="text-xs text-muted-foreground">
        Direct (off-PadSplit) members get their own login for maintenance requests, messages,
        cleaning and move-out notices. Turn it on to invite members from their profile.
      </p>
      <div className="flex items-center justify-between gap-3 rounded-md border p-3">
        <label htmlFor="switch-member-portal-opt-in" className="text-sm text-foreground">
          Use the member portal for this account
        </label>
        <Switch
          id="switch-member-portal-opt-in"
          checked={optedIn}
          disabled={!state || !canManage || mutation.isPending}
          onCheckedChange={(v) => mutation.mutate(v)}
          data-testid="switch-member-portal-opt-in"
        />
      </div>
      {state && !canManage && (
        <p className="text-xs text-muted-foreground" data-testid="text-member-portal-opt-in-owner-only">
          Only the account owner or a team admin can change this.
        </p>
      )}
      {optedIn && (
        <p className="text-xs text-muted-foreground">
          Turning it off stops new invites. Members you already invited keep their access.
        </p>
      )}
    </div>
  );
}

/** Members page header control: a "Member portal" button opening the opt-in panel. */
export function MemberPortalOptInButton() {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid="button-member-portal-settings">
          <UserPlus className="h-4 w-4 mr-1" />
          Member portal
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        {open && <MemberPortalOptInPanel />}
      </PopoverContent>
    </Popover>
  );
}
